import React from 'react';
import { Button, CheckChunkierIcon } from '@eventbrite/marmalade';
import styles from './FollowButton.module.scss';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

// Local imports
import { FollowButtonVariant, type FollowButtonProps, FOLLOW_ACTION, UNFOLLOW_ACTION } from './types';
import { useFollowButtonActions } from './hooks/useFollowButtonActions';
import { useOrganizerFollowStatus } from '../../contexts/FollowOrganizerContext';
import { useUserData } from '@/src/listings/hooks/useUserData/useUserData';

const FollowButtonComponent = ({ organizer, variant = FollowButtonVariant.Default, pageArea }: FollowButtonProps) => {
  const { isFollowing, loading } = useOrganizerFollowStatus();
  const { userId } = useUserData();
  const isAuthenticated = !!userId;
  const { handleFollowAction } = useFollowButtonActions(organizer.id, isAuthenticated);

  const isLoading = loading;
  const handleFollow = () => handleFollowAction(FOLLOW_ACTION, pageArea);
  const handleUnfollow = () => handleFollowAction(UNFOLLOW_ACTION, pageArea);

  const buttonConfig = isFollowing
    ? {
        variant: 'secondary' as const,
        icon: <CheckChunkierIcon />,
        text: <FormattedMessage id="Following" />,
        onClick: handleUnfollow,
      }
    : {
        variant: 'primary' as const,
        icon: null,
        text: <FormattedMessage id="Follow" />,
        onClick: handleFollow,
      };

  const buttonClasses = classNames(styles.followButton, {
    [styles.following]: isFollowing,
    [styles.organizer]: variant === FollowButtonVariant.Organizer,
  });

  const renderButtonContent = () => (
    <div className={styles.textContainer}>
      {buttonConfig.icon} {buttonConfig.text}
    </div>
  );

  const buttonVariant = isLoading ? 'secondary' : buttonConfig.variant;
  const isDisabled = isLoading;
  const buttonOnClick = isLoading ? undefined : buttonConfig.onClick;

  return (
    <Button variant={buttonVariant} className={buttonClasses} onClick={buttonOnClick} disabled={isDisabled}>
      {renderButtonContent()}
    </Button>
  );
};

export const FollowButton = FollowButtonComponent;
export { FollowButtonVariant } from './types';
